import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import {
  ChevronRight,
  Shield,
  Wallet,
  CheckCircle2,
  FileText,
  Users,
  AlertCircle,
  Building2
} from "lucide-react";
import { useState } from "react";

// FAQ data for the page and JSON-LD
const faqData = [
  {
    question: "Does SwarmCitadel replace x402?",
    answer: "No. x402 is the payment rail. SwarmCitadel is the policy and audit layer that sits between the agent and the rail."
  },
  {
    question: "Which chains and stablecoins will be supported?",
    answer: "x402 is chain-agnostic. SwarmCitadel's x402 scheme will follow the networks the standard supports, starting with EVM chains. Details will be published with the release."
  },
  {
    question: "Can an agent bypass the policy engine and pay directly?",
    answer: "Not when its funds live in a SwarmCitadel-governed vault. The agent never holds a signing key; it only holds the ability to request a payment."
  },
  {
    question: "Is this live today?",
    answer: "The x402 payment scheme is in development. Request a demo to join the preview."
  },
  {
    question: "Does this work with agent frameworks like OpenClaw?",
    answer: "Yes. SwarmCitadel governs any agent that can forward a 402 challenge over HTTP, regardless of framework."
  }
];

// JSON-LD schemas
const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

const softwareAppJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "SwarmCitadel",
  "applicationCategory": "BusinessApplication",
  "description": "Govern x402 agent payments with policy enforcement before signature, budgets, counterparty allowlists and tamper-evident audit trails. The governance layer for the open x402 standard.",
  "operatingSystem": "Web",
  "url": "https://swarmcitadel.com/x402"
};

// Stepper data for the 7-step flow
const policyFlowSteps = [
  {
    number: 1,
    title: "Agent receives challenge",
    description: "Agent requests a resource and receives an x402 payment challenge."
  },
  {
    number: 2,
    title: "Forward to treasury",
    description: "Agent forwards the challenge to its SwarmCitadel treasury instead of paying directly."
  },
  {
    number: 3,
    title: "Policy evaluation",
    description: "Policy engine evaluates the challenge: per-call ceiling, daily budget, counterparty allowlist, spend category, and the Reasoning Quality Gate on why the agent wants this purchase. Most restrictive rule wins."
  },
  {
    number: 4,
    title: "Sign and return",
    description: "Approved payments are signed from a governed vault and returned to the agent to retry the request."
  },
  {
    number: 5,
    title: "Escalate if denied",
    description: "Denied payments escalate to a human with the agent's reasoning attached."
  },
  {
    number: 6,
    title: "Audit trail",
    description: "Every decision is written to the tamper-evident audit trail, tied to the on-chain payment."
  },
  {
    number: 7,
    title: "Unified visibility",
    description: "Finance sees one treasury, one ledger, and spend by agent, vendor and purpose in real time."
  }
];

// Capabilities cards
const capabilities = [
  {
    title: "Spend policy",
    description: "Per-call, daily and monthly ceilings per agent, per team, per vendor.",
    icon: Wallet
  },
  {
    title: "Counterparty control",
    description: "Allowlists and denylists for x402 endpoints, with velocity caps.",
    icon: Shield
  },
  {
    title: "Reasoning Quality Gate",
    description: "Evaluates the agent's stated justification before funds move.",
    icon: AlertCircle
  },
  {
    title: "Audit trail",
    description: "Challenge, policy result, reasoning score and outcome, linked to the transaction hash.",
    icon: FileText
  }
];

// Who it is for
const targetAudiences = [
  "Fleets of research and data-enrichment agents buying API access",
  "Procurement agents paying for services",
  "Any regulated operator that needs to show an auditor why an autonomous system spent money"
];

export const X402Page = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="pt-24" data-testid="x402-page">
      <Seo
        title="x402 Agent Payments Governance | SwarmCitadel"
        description="Govern x402 agent payments with policy enforcement before signature, budgets, counterparty allowlists and tamper-evident audit trails. The governance layer for the open x402 standard."
        path="/x402"
      >
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareAppJsonLd)}</script>
      </Seo>

      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-swarm-violet/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            In development
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
            Governed x402 Payments
            <br />
            <span className="text-gradient-safe">for AI Agents</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-3xl">
            x402 lets any agent pay any API over HTTP. SwarmCitadel decides whether it should, and proves it afterward.
          </p>
        </div>
      </section>

      {/* What x402 is */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            The open payment standard for agents
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-400 leading-relaxed mb-4">
              x402 is an open standard, governed by the x402 Foundation under the Linux Foundation, that turns the unused HTTP 402 status code into a payment request. A server adds one line of middleware. An agent that calls the endpoint without payment receives a 402 response with the price, pays in stablecoins, and retries. No account, no API key, no invoice.
            </p>
            <p className="text-slate-400 leading-relaxed">
              It is blockchain-agnostic and charges no protocol fee. x402.org reported 75M+ transactions and $24M+ volume in the 30 days to October 2026.
            </p>
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="py-16 md:py-20 bg-swarm-darker/50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Effortless payments make invisible spend
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-400 leading-relaxed mb-4">
              When fifty agents each hit paid endpoints hundreds of times a day, the finance team has fifty wallets, no aggregate view, and no control until a balance reaches zero. A prompt injection in one agent can drain its wallet by paying a hostile endpoint.
            </p>
            <p className="text-slate-400 leading-relaxed">
              x402 was designed to answer how an agent pays. It deliberately says nothing about whether it should.
            </p>
          </div>
        </div>
      </section>

      {/* How SwarmCitadel governs x402 - 7-step flow */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 text-center">
            Policy before signature
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            SwarmCitadel evaluates every x402 payment challenge before signing.
          </p>

          {/* Vertical Stepper */}
          <div className="relative max-w-3xl mx-auto">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-swarm-border" />
            
            <div className="space-y-8">
              {policyFlowSteps.map((step, index) => (
                <div key={step.number} className="relative flex gap-6">
                  {/* Step number circle */}
                  <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-mono font-bold text-lg ${
                    index === 0 ? 'bg-swarm-violet text-white' :
                    index === policyFlowSteps.length - 1 ? 'bg-swarm-green text-white' :
                    'bg-swarm-darker border-2 border-swarm-cyan text-swarm-cyan'
                  }`}>
                    {step.number}
                  </div>
                  
                  {/* Step content */}
                  <div className="glass-card p-5 flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-20 bg-swarm-darker/50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Capabilities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.map((capability) => {
              const Icon = capability.icon;
              return (
                <div key={capability.title} className="glass-card p-6 border-swarm-cyan/20 hover:border-swarm-cyan/40 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-swarm-cyan/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-swarm-cyan" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {capability.title}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {capability.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who it is for */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            Who it is for
          </h2>
          
          <div className="space-y-4">
            {targetAudiences.map((audience, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-swarm-violet/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {index === 0 && <Users className="w-4 h-4 text-swarm-violet" />}
                  {index === 1 && <Building2 className="w-4 h-4 text-swarm-violet" />}
                  {index === 2 && <FileText className="w-4 h-4 text-swarm-violet" />}
                </div>
                <p className="text-slate-300 leading-relaxed">
                  {audience}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-swarm-darker/50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index} 
                className="glass-card border-swarm-border overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-toggle-${index}`}
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  <ChevronRight className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                    expandedFaq === index ? 'rotate-90' : ''
                  }`} />
                </button>
                
                {expandedFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-slate-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-lg font-semibold text-white mb-4">Learn more</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/product" className="text-swarm-cyan hover:underline text-sm">
              Platform overview
            </Link>
            <span className="text-slate-600">|</span>
            <Link to="/use-cases#x402" className="text-swarm-cyan hover:underline text-sm">
              Use cases for AI agent governance
            </Link>
            <span className="text-slate-600">|</span>
            <Link to="/governance" className="text-swarm-cyan hover:underline text-sm">
              MGF compliance architecture
            </Link>
            <span className="text-slate-600">|</span>
            <Link to="/docs/x402" className="text-swarm-cyan hover:underline text-sm">
              x402 integration docs preview
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Join the preview
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            The x402 payment scheme is in development. Request a demo to be among the first to govern agent payments.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-glow" data-testid="x402-cta-demo">
                Request a demo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/docs/x402">
              <Button variant="outline" className="btn-outline" data-testid="x402-cta-docs">
                Read the docs preview
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default X402Page;
